import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { View } from '@instructure/ui-view';
import { IconArrowOpenEndSolid } from '@instructure/ui-icons';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { Children } from '@instructure/ui-prop-types';
import { testable } from '@instructure/ui-testable';
import { BreadcrumbLink } from './BreadcrumbLink';
var styles = {
  componentId: 'fkqjh',
  template: function template(theme) {
    return "\n\n.fkqjh_bGBk{align-items:center;display:flex;font-family:".concat(theme.fontFamily || 'inherit', ";list-style-type:none;margin:0;overflow:visible;padding:0}\n\n.fkqjh_dWNq{box-sizing:border-box;position:relative}\n\n.fkqjh_dWNq:last-child{-webkit-padding-end:0;padding-inline-end:0}\n\n[dir=ltr] .fkqjh_dWNq:last-child{padding-right:0}\n\n[dir=rtl] .fkqjh_dWNq:last-child{padding-left:0}\n\n.fkqjh_fatK{box-sizing:border-box;color:").concat(theme.separatorColor || 'inherit', ";position:absolute;top:50%}\n\n.fkqjh_doqw .fkqjh_dWNq{-webkit-padding-end:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "*2);-webkit-padding-start:0;font-size:").concat(theme.smallFontSize || 'inherit', ";padding-inline-end:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "*2);padding-inline-start:0}\n\n[dir=ltr] .fkqjh_doqw .fkqjh_dWNq{padding-left:0;padding-right:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "*2)}\n\n[dir=rtl] .fkqjh_doqw .fkqjh_dWNq{padding-left:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "*2);padding-right:0}\n\n.fkqjh_doqw .fkqjh_dWNq .fkqjh_fatK{font-size:").concat(theme.smallSeparatorFontSize || 'inherit', ";inset-inline-end:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "/2);inset-inline-start:auto;margin-top:calc(-1*").concat(theme.smallSeparatorFontSize || 'inherit', "/2)}\n\n[dir=ltr] .fkqjh_doqw .fkqjh_dWNq .fkqjh_fatK{left:auto;right:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "/2)}\n\n[dir=rtl] .fkqjh_doqw .fkqjh_dWNq .fkqjh_fatK{left:calc(").concat(theme.smallSeparatorFontSize || 'inherit', "/2);right:auto}\n\n.fkqjh_ycrn .fkqjh_dWNq{-webkit-padding-end:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "*2);-webkit-padding-start:0;font-size:").concat(theme.mediumFontSize || 'inherit', ";padding-inline-end:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "*2);padding-inline-start:0}\n\n[dir=ltr] .fkqjh_ycrn .fkqjh_dWNq{padding-left:0;padding-right:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "*2)}\n\n[dir=rtl] .fkqjh_ycrn .fkqjh_dWNq{padding-left:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "*2);padding-right:0}\n\n.fkqjh_ycrn .fkqjh_dWNq .fkqjh_fatK{font-size:").concat(theme.mediumSeparatorFontSize || 'inherit', ";inset-inline-end:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "/2);inset-inline-start:auto;margin-top:calc(-1*").concat(theme.mediumSeparatorFontSize || 'inherit', "/2)}\n\n[dir=ltr] .fkqjh_ycrn .fkqjh_dWNq .fkqjh_fatK{left:auto;right:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "/2)}\n\n[dir=rtl] .fkqjh_ycrn .fkqjh_dWNq .fkqjh_fatK{left:calc(").concat(theme.mediumSeparatorFontSize || 'inherit', "/2);right:auto}\n\n.fkqjh_cMDj .fkqjh_dWNq{-webkit-padding-end:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "*2);-webkit-padding-start:0;font-size:").concat(theme.largeFontSize || 'inherit', ";padding-inline-end:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "*2);padding-inline-start:0}\n\n[dir=ltr] .fkqjh_cMDj .fkqjh_dWNq{padding-left:0;padding-right:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "*2)}\n\n[dir=rtl] .fkqjh_cMDj .fkqjh_dWNq{padding-left:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "*2);padding-right:0}\n\n.fkqjh_cMDj .fkqjh_dWNq .fkqjh_fatK{font-size:").concat(theme.largeSeparatorFontSize || 'inherit', ";inset-inline-end:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "/2);inset-inline-start:auto;margin-top:calc(-1*").concat(theme.largeSeparatorFontSize || 'inherit', "/2)}\n\n[dir=ltr] .fkqjh_cMDj .fkqjh_dWNq .fkqjh_fatK{left:auto;right:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "/2)}\n\n[dir=rtl] .fkqjh_cMDj .fkqjh_dWNq .fkqjh_fatK{left:calc(").concat(theme.largeSeparatorFontSize || 'inherit', "/2);right:auto}");
  },
  'root': 'fkqjh_bGBk',
  'crumb': 'fkqjh_dWNq',
  'separator': 'fkqjh_fatK',
  'small': 'fkqjh_doqw',
  'medium': 'fkqjh_ycrn',
  'large': 'fkqjh_cMDj'
};
import theme from './theme';
/**
---
category: components
---
**/

var Breadcrumb = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Breadcrumb, _Component);

  var _super = _createSuper(Breadcrumb);

  function Breadcrumb() {
    _classCallCheck(this, Breadcrumb);

    return _super.apply(this, arguments);
  }

  _createClass(Breadcrumb, [{
    key: "renderChildren",
    value: function renderChildren() {
      var numChildren = this.props.children ? this.props.children.length : 0;
      var style = {
        maxWidth: "".concat(Math.floor(100 / numChildren), "%")
      };
      return React.Children.map(this.props.children, function (child, index) {
        return /*#__PURE__*/React.createElement("li", {
          className: styles.crumb,
          style: style
        }, child, index < numChildren - 1 && /*#__PURE__*/React.createElement(IconArrowOpenEndSolid, {
          color: "auto",
          className: styles.separator
        }));
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _classes;

      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles[this.props.size], true), _classes);
      return /*#__PURE__*/React.createElement(View, {
        role: "navigation",
        as: "div",
        margin: this.props.margin,
        "aria-label": this.props.label
      }, /*#__PURE__*/React.createElement("ol", {
        className: classnames(classes)
      }, this.renderChildren()));
    }
  }]);

  Breadcrumb.displayName = "Breadcrumb";
  return Breadcrumb;
}(Component), _class2.propTypes = {
  /**
  * children of type Breadcrumb.Link
  */
  children: Children.oneOf([BreadcrumbLink]),

  /**
  * An accessible label for the navigation
  */
  label: PropTypes.string.isRequired,

  /**
  * Sets the font-size of the breadcrumb text
  */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
  * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `margin="small auto large"`.
  */
  margin: ThemeablePropTypes.spacing
}, _class2.defaultProps = {
  size: 'medium',
  children: null,
  margin: void 0
}, _class2.Link = BreadcrumbLink, _temp)) || _class) || _class);
export default Breadcrumb;
export { Breadcrumb, BreadcrumbLink };